import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Group, Me, Membership, Meta, Tenant, TenantSettings } from '@shared/types';
import { stageVocabulary } from '@shared/kpi';
import { DEFAULT_BUSINESS_HOURS, WEEKDAY_NAMES, weeklyHours, type BusinessHours } from '@shared/businessHours';
import { DEFAULT_HOLIDAY_RULES, HOLIDAY_KINDS, HOLIDAY_RULE_LABELS, type Holiday, type HolidayRule, type HolidayRules } from '@shared/holidays';
import { formatDateTime } from '@shared/dates';
import { api, setCurrentTenant } from '../api';
import { errorText, useSaveGroups, useToast } from '../store';
import { PillLabel } from './StagePill';
import { installEvent, isStandalone, onInstallChange, type InstallEvent } from '../pwa';
import { Icons, LegalLinks, Spinner } from './ui';
import { NotifySettings } from './Notify';
import { DEFAULT_ROLE_ID, memberGroupIds, resolveNotifyRoles } from '@shared/notify';

export function SettingsPage({ meta, me, onSwitchTenant }: { meta: Meta; me: Me; onSwitchTenant: (id: string) => void }) {
  const admin = meta.me.role === 'admin';
  return (
    <div className="mx-auto grid max-w-3xl grid-cols-[minmax(0,1fr)] gap-6 p-4">
      <section className="card p-4">
        <h2 className="mb-2 text-base font-semibold">המשתמש שלי</h2>
        <dl className="grid grid-cols-[120px_1fr] gap-y-1 text-sm">
          <dt className="text-ink-3">שם</dt><dd>{meta.me.displayName ?? '—'}</dd>
          <dt className="text-ink-3">אימייל</dt><dd className="ltr">{meta.me.email ?? '—'}</dd>
          <dt className="text-ink-3">לקוח</dt><dd>{meta.tenant.name}</dd>
          <dt className="text-ink-3">תפקיד</dt><dd>{meta.me.superAdmin ? 'מפעיל המערכת' : admin ? 'מנהל' : 'משתמש'}</dd>
          <dt className="text-ink-3">משתמש CRM</dt><dd>{meta.users.find((u) => u.id === meta.me.ghlUserId)?.name ?? <span className="text-danger">לא מקושר — הערות לא יירשמו על שמך</span>}</dd>
        </dl>
      </section>
      <NotifySettings />
      <GroupsManager meta={meta} readOnly={!admin} />
      {admin && <MembersManager meta={meta} />}
      {admin && <TenantSettingsPanel meta={meta} />}
      {admin && <SyncPanel meta={meta} />}
      {me.superAdmin && <TenantsPanel me={me} current={meta.tenant} onSwitchTenant={onSwitchTenant} />}
      <InstallPanel />
      <LegalLinks className="pb-4" />
    </div>
  );
}

// הגדרות › התקנת האפליקציה — Chrome's prompt where offered, the share-sheet
// route on iOS, nothing once the app already runs installed
function InstallPanel() {
  const [evt, setEvt] = useState<InstallEvent | null>(null);
  useEffect(() => {
    if (isStandalone()) return;
    setEvt(installEvent());
    return onInstallChange(setEvt);
  }, []);
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
  if (isStandalone() || (!evt && !ios)) return null;
  return (
    <section className="card p-4">
      <h2 className="mb-2 text-base font-semibold">התקנת האפליקציה</h2>
      {evt ? (
        <>
          <button className="btn btn-primary btn-sm" onClick={async () => { const e = evt; setEvt(null); await e.prompt(); await e.userChoice.catch(() => null); }}>התקנה במכשיר</button>
          <p className="mt-2 text-sm text-ink-3">הלידים ייפתחו כאפליקציה עצמאית, עם אייקון במסך הבית.</p>
        </>
      ) : (
        <p className="text-sm text-ink-3">באייפון: כפתור השיתוף בסרגל הדפדפן ← &quot;הוספה למסך הבית&quot;.</p>
      )}
    </section>
  );
}

function GroupsManager({ meta, readOnly }: { meta: Meta; readOnly: boolean }) {
  const [groups, setGroups] = useState<Group[]>(meta.groups);
  useEffect(() => setGroups(meta.groups), [meta.groups]);
  const save = useSaveGroups();
  const vocab = stageVocabulary(meta.pipelines);
  const dirty = JSON.stringify(groups) !== JSON.stringify(meta.groups);
  const update = (i: number, patch: Partial<Group>) => setGroups((g) => g.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i: number, d: -1 | 1) => setGroups((g) => { const n = g.slice(); const j = i + d; if (j < 0 || j >= n.length) return g; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const assigned = new Map<string, string>();
  groups.forEach((g) => g.stageNames.forEach((s) => { if (!assigned.has(s)) assigned.set(s, g.name); }));
  return (
    <section className="card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold">קבוצות</h2>
        {!readOnly && <div className="flex gap-2">
          <button className="btn btn-sm" onClick={() => setGroups((g) => [...g, { id: '', name: 'קבוצה חדשה', stageNames: [], sortOrder: g.length }])}><Icons.Plus size={14} /> הוסף קבוצה</button>
          <button className="btn btn-primary btn-sm" disabled={!dirty || save.isPending} onClick={() => save.mutate(groups)}>שמור</button>
        </div>}
      </div>
      <p className="mb-3 text-xs text-ink-3">קבוצה היא אוסף של סטטוסים. סטטוס יכול להופיע בקבוצה אחת בלבד; סטטוסים שלא שויכו יופיעו תחת "אחר".</p>
      {groups.length === 0 && <p className="text-sm text-ink-3">אין קבוצות עדיין — כל הלידים יוצגו תחת "אחר".</p>}
      <div className="grid gap-3">
        {groups.map((g, i) => (
          <div key={i} className="rounded-lg border border-line p-3">
            <div className="mb-2 flex items-center gap-2">
              {readOnly ? <span className="font-semibold">{g.name}</span> : <input className="input h-9 min-h-0 max-w-xs" value={g.name} onChange={(e) => update(i, { name: e.target.value })} aria-label="שם הקבוצה" />}
              {!readOnly && <span className="ms-auto flex gap-1">
                <button className="btn btn-sm" onClick={() => move(i, -1)} disabled={i === 0} aria-label="הזז למעלה">↑</button>
                <button className="btn btn-sm" onClick={() => move(i, 1)} disabled={i === groups.length - 1} aria-label="הזז למטה">↓</button>
                <button className="btn btn-sm btn-danger" onClick={() => setGroups((x) => x.filter((_, j) => j !== i))}>מחק</button>
              </span>}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {vocab.map((s) => {
                const on = g.stageNames.includes(s);
                const elsewhere = !on && assigned.has(s);
                if (readOnly && !on) return null;
                return (
                  <button key={s} type="button" disabled={readOnly || elsewhere} title={elsewhere ? `כבר ב"${assigned.get(s)}"` : undefined}
                    className={`rounded-full border px-1 py-0.5 ${on ? 'border-accent' : 'border-transparent opacity-60'} disabled:opacity-30`}
                    onClick={() => update(i, { stageNames: on ? g.stageNames.filter((x) => x !== s) : [...g.stageNames, s] })}>
                    <PillLabel name={s} />
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function MembersManager({ meta }: { meta: Meta }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [members, setMembers] = useState<Membership[]>(meta.members);
  const [email, setEmail] = useState('');
  const roles = resolveNotifyRoles(meta.tenant.settings.notifyRoles, meta.groups);
  // a new member's starting notifications (they change them later themselves): the default role's groups, and tasks
  const [groups, setGroups] = useState<string[]>(() => [...memberGroupIds([DEFAULT_ROLE_ID], roles)]);
  const [tasks, setTasks] = useState(true);
  useEffect(() => setMembers(meta.members), [meta.members]);
  const run = async (fn: () => Promise<Membership[]>, msg = 'נשמר') => {
    try { setMembers(await fn()); void qc.invalidateQueries({ queryKey: ['meta'] }); toast(msg); } catch (e) { toast(errorText(e), 'error'); }
  };
  const heard = (m: Membership) => {
    const ids = new Set(m.notifyGroups ?? memberGroupIds(m.notifyRoles, roles));
    return [...(m.notifyTasks ? ['משימות'] : []), ...meta.groups.filter((g) => ids.has(g.id)).map((g) => g.name)];
  };
  return (
    <section className="card p-4">
      <h2 className="mb-1 text-base font-semibold">משתמשים</h2>
      <p className="mb-3 text-xs text-ink-3">הזמנה נעשית לפי כתובת אימייל: מי שנכנס עם אימייל זה יקבל גישה ללקוח הזה. הקישור למשתמש CRM קובע על שם מי יירשמו ההערות (אימייל זהה מקושר אוטומטית).</p>
      <form className="mb-3 grid gap-2 rounded-lg border border-line p-3" onSubmit={(e) => {
        e.preventDefault();
        if (email.trim()) run(() => api.upsertMember(email.trim(), { role: 'user', notifyGroups: groups, notifyTasks: tasks }), 'המשתמש הוזמן').then(() => setEmail(''));
      }}>
        <div className="flex flex-wrap gap-2">
          <input className="input ltr max-w-xs" dir="ltr" type="email" placeholder="email@company.co.il" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="אימייל להזמנה" />
          <button className="btn btn-primary" type="submit" disabled={!email.trim()}><Icons.Plus size={16} /> הזמן</button>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="text-xs text-ink-3">התראות התחלתיות:</span>
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={tasks} onChange={(e) => setTasks(e.target.checked)} />משימות</label>
          {meta.groups.map((g) => (
            <label key={g.id} className="flex items-center gap-1.5"><input type="checkbox" checked={groups.includes(g.id)}
              onChange={(e) => setGroups((x) => e.target.checked ? [...x, g.id] : x.filter((id) => id !== g.id))} />{g.name}</label>
          ))}
        </div>
        <p className="text-xs text-ink-3">אחרי ההזמנה כל משתמש קובע בעצמו את ההתראות שלו.</p>
      </form>
      <div className="-mx-4 overflow-x-auto px-4"><table className="w-full min-w-max text-sm">
        <thead><tr className="text-xs text-ink-3"><th className="py-1 text-start">אימייל</th><th className="text-start">שם</th><th className="text-start">משתמש CRM</th><th className="text-start">תפקיד</th><th className="text-start">התראות</th><th /></tr></thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.email} className="border-t border-line">
              <td className="ltr py-2">{m.email}{!m.clerkUserId && <span className="ms-2 rounded bg-amber-50 px-1 text-[11px] text-amber-700">טרם נכנס</span>}</td>
              <td>{m.displayName ?? '—'}</td>
              <td><select className="input h-9 min-h-0" value={m.ghlUserId ?? ''} onChange={(e) => run(() => api.upsertMember(m.email, { ghlUserId: e.target.value || null }))} aria-label="משתמש CRM"><option value="">— לא מקושר —</option>{meta.users.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select></td>
              <td><select className="input h-9 min-h-0" value={m.role} onChange={(e) => run(() => api.upsertMember(m.email, { role: e.target.value as 'user' | 'admin' }))} aria-label="תפקיד"><option value="user">משתמש</option><option value="admin">מנהל</option></select></td>
              <td className="max-w-56 whitespace-normal pe-2 text-xs text-ink-2">{heard(m).join(', ') || '—'}</td>
              <td><button className="btn btn-sm btn-danger" disabled={m.email === meta.me.email} onClick={() => confirm(`להסיר את ${m.email}?`) && run(() => api.removeMember(m.email), 'הוסר')}>הסר</button></td>
            </tr>
          ))}
        </tbody>
      </table></div>
    </section>
  );
}

/** Per-tenant configuration: name, which pipelines to show, the lead-date rule, KPI buckets. */
function TenantSettingsPanel({ meta }: { meta: Meta }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(meta.tenant.name);
  const [s, setS] = useState<TenantSettings>(meta.tenant.settings);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setName(meta.tenant.name); setS(meta.tenant.settings); }, [meta.tenant]);
  const dirty = name !== meta.tenant.name || JSON.stringify(s) !== JSON.stringify(meta.tenant.settings);
  const toggle = (key: 'trackedPipelineIds' | 'forecastDatePipelineIds', v: string) =>
    setS((x) => ({ ...x, [key]: x[key].includes(v) ? x[key].filter((y) => y !== v) : [...x[key], v] }));
  const save = async () => {
    setSaving(true);
    try { await api.updateSettings({ name, settings: s }); await qc.invalidateQueries({ queryKey: ['meta'] }); await qc.invalidateQueries({ queryKey: ['me'] }); toast('ההגדרות נשמרו; המטמון מתעדכן ברקע'); }
    catch (e) { toast(errorText(e), 'error'); }
    finally { setSaving(false); }
  };
  return (
    <section className="card grid gap-4 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">הגדרות הלקוח</h2>
        <button className="btn btn-primary btn-sm" disabled={!dirty || saving} onClick={save}>{saving ? <Spinner /> : 'שמור'}</button>
      </div>
      <label className="grid gap-1 text-sm"><span>שם הלקוח (מוצג בכותרת)</span><input className="input max-w-sm" value={name} onChange={(e) => setName(e.target.value)} /></label>
      <div>
        <div className="mb-1 text-sm">פייפליינים שמוצגים ב-Piper <span className="text-ink-3">(ריק = כולם)</span></div>
        <div className="grid gap-1">
          {meta.allPipelines.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={s.trackedPipelineIds.includes(p.id)} onChange={() => toggle('trackedPipelineIds', p.id)} />{p.name} <span className="text-xs text-ink-3">({p.stages.length} שלבים)</span></label>
          ))}
        </div>
      </div>
      <div>
        <div className="mb-1 text-sm">תאריך הליד נלקח מ"תאריך סגירה צפוי" בפייפליינים אלה <span className="text-ink-3">(לנתונים שיובאו בבת אחת)</span></div>
        <div className="grid gap-1">
          {meta.allPipelines.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={s.forecastDatePipelineIds.includes(p.id)} onChange={() => toggle('forecastDatePipelineIds', p.id)} />{p.name}</label>
          ))}
        </div>
      </div>
      <label className="grid gap-1 text-sm">
        <span>הודעת פתיחה בוואטסאפ <span className="text-ink-3">(נכתבת מראש כשלוחצים על כפתור הוואטסאפ; אפשר לשנות לפני השליחה. ריק = צ'אט ריק)</span></span>
        <textarea className="input min-h-20 py-2" dir="auto" maxLength={1000} value={s.whatsappMessage} onChange={(e) => setS((x) => ({ ...x, whatsappMessage: e.target.value }))} />
      </label>
      <BusinessHoursEditor value={s.businessHours} onChange={(businessHours) => setS((x) => ({ ...x, businessHours }))} />
      <HolidayRulesEditor value={s.holidayRules} holidays={meta.holidays} onChange={(holidayRules) => setS((x) => ({ ...x, holidayRules }))} />
    </section>
  );
}

/** Opening hours per weekday; they drive "זמן המתנה ממוצע (בשעות הפעילות)". Israel time. */
function BusinessHoursEditor({ value, onChange }: { value: BusinessHours; onChange: (v: BusinessHours) => void }) {
  const set = (day: number, d: BusinessHours['week'][number]) => onChange({ ...value, week: value.week.map((x, i) => (i === day ? d : x)) });
  const invalid = value.week.some((d) => d && d[0] >= d[1]);
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-sm">שעות פעילות <span className="text-ink-3">(לחישוב זמן ההמתנה; שעון ישראל)</span></span>
        <button type="button" className="text-xs text-accent" onClick={() => onChange(DEFAULT_BUSINESS_HOURS)}>ברירת מחדל</button>
      </div>
      <div className="grid gap-1.5">
        {value.week.map((d, i) => (
          <div key={i} className="grid grid-cols-[4.75rem_minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 text-sm desk:grid-cols-[6rem_8rem_auto_8rem] desk:justify-start">
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!d} onChange={(e) => set(i, e.target.checked ? (DEFAULT_BUSINESS_HOURS.week[i] ?? ['08:30', '18:30']) : null)} />{WEEKDAY_NAMES[i]}</label>
            {d ? (
              <>
                <TimeSelect value={d[0]} bad={d[0] >= d[1]} label={`פתיחה ביום ${WEEKDAY_NAMES[i]}`} onChange={(v) => set(i, [v, d[1]])} />
                <span className="text-ink-3">עד</span>
                <TimeSelect value={d[1]} bad={d[0] >= d[1]} label={`סגירה ביום ${WEEKDAY_NAMES[i]}`} onChange={(v) => set(i, [d[0], v])} />
              </>
            ) : <span className="col-span-3 text-ink-3">סגור</span>}
          </div>
        ))}
      </div>
      <p className="mt-1 text-xs text-ink-3">{invalid ? 'שעת סגירה לפני שעת הפתיחה — היום הזה לא ייספר.' : `${weeklyHours(value).toLocaleString('he-IL')} שעות פעילות בשבוע.`}</p>
    </div>
  );
}

/** Every quarter hour, 00:00–23:45, always 24-hour (a native time input shows AM/PM on phones set to 12-hour). */
const QUARTER_HOURS = Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`);

function TimeSelect({ value, onChange, label, bad }: { value: string; onChange: (v: string) => void; label: string; bad?: boolean }) {
  const options = QUARTER_HOURS.includes(value) ? QUARTER_HOURS : [...QUARTER_HOURS, value].sort(); // keep an off-grid stored time
  return (
    <select className={`input ltr h-9 min-h-0 w-full min-w-0 px-2 tabular-nums ${bad ? 'border-danger' : ''}`} value={value} aria-label={label} onChange={(e) => onChange(e.target.value)}>
      {options.map((t) => <option key={t} value={t}>{t}</option>)}
    </select>
  );
}

/** How each kind of Israeli holiday changes the opening hours; the dates come from Hebcal. */
function HolidayRulesEditor({ value, holidays, onChange }: { value: HolidayRules; holidays: Holiday[]; onChange: (v: HolidayRules) => void }) {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Jerusalem' }); // YYYY-MM-DD
  const next = holidays.filter((h) => h.date >= today).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 3);
  const fmt = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}`;
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-sm">חגים <span className="text-ink-3">(מה קורה לשעות הפעילות)</span></span>
        <button type="button" className="text-xs text-accent" onClick={() => onChange(DEFAULT_HOLIDAY_RULES)}>ברירת מחדל</button>
      </div>
      <div className="grid gap-1.5">
        {HOLIDAY_KINDS.map(({ kind, label, example }) => (
          <div key={kind} className="grid grid-cols-[minmax(0,1fr)_10rem] items-center gap-2 text-sm desk:grid-cols-[16rem_12rem] desk:justify-start">
            <span className="min-w-0">{label}{example && <span className="block truncate text-xs text-ink-3">{example}</span>}</span>
            <select className="input h-9 min-h-0 px-2" value={value[kind]} aria-label={label} onChange={(e) => onChange({ ...value, [kind]: e.target.value as HolidayRule })}>
              {(Object.keys(HOLIDAY_RULE_LABELS) as HolidayRule[]).map((r) => <option key={r} value={r}>{HOLIDAY_RULE_LABELS[r]}</option>)}
            </select>
          </div>
        ))}
      </div>
      <p className="mt-1 text-xs text-ink-3">
        {holidays.length === 0 ? 'לוח החגים לא נטען כרגע; זמני ההמתנה מחושבים לפי השבוע הרגיל.'
          : next.length ? `הקרובים: ${next.map((h) => `${h.name} ${fmt(h.date)} (${HOLIDAY_RULE_LABELS[value[h.kind]]})`).join(' · ')}` : ''}
        {' '}נתוני חגים: <a className="underline" href="https://www.hebcal.com/" target="_blank" rel="noopener noreferrer">Hebcal.com</a> (CC BY 4.0).
      </p>
    </div>
  );
}

function SyncPanel({ meta }: { meta: Meta }) {
  const toast = useToast();
  const qc = useQueryClient();
  const s = meta.sync;
  const run = async (fn: () => Promise<unknown>, msg: string) => { try { await fn(); toast(msg); setTimeout(() => qc.invalidateQueries({ queryKey: ['meta'] }), 1500); } catch (e) { toast(errorText(e), 'error'); } };
  const install = async () => { try { const { url } = await api.oauthStart(); location.href = url; } catch (e) { toast(errorText(e), 'error'); } };
  return (
    <section className="card p-4">
      <h2 className="mb-2 text-base font-semibold">חיבור ל-CRM וסנכרון</h2>
      <dl className="mb-3 grid grid-cols-[140px_1fr] gap-y-1 text-sm">
        <dt className="text-ink-3">מזהה חשבון CRM</dt><dd className="ltr">{meta.tenant.locationId}</dd>
        <dt className="text-ink-3">אפליקציה מותקנת</dt><dd>{s.installed ? 'כן' : <span className="text-danger">לא</span>}</dd>
        <dt className="text-ink-3">טוקן תקין</dt><dd>{s.tokenOk ? 'כן' : <span className="text-danger">לא — יש להתקין מחדש</span>}</dd>
        <dt className="text-ink-3">סריקה מלאה אחרונה</dt><dd className="ltr">{formatDateTime(s.lastFullSync)}{s.running ? ' (רץ עכשיו…)' : ''}</dd>
        <dt className="text-ink-3">במטמון</dt><dd>{s.contacts} אנשי קשר · {s.opportunities} הזדמנויות{s.tasks != null ? ` · ${s.tasks} משימות` : ''}</dd>
        {s.lastError && <><dt className="text-ink-3">שגיאה אחרונה</dt><dd className="ltr break-all text-danger">{s.lastError}</dd></>}
      </dl>
      <div className="flex flex-wrap gap-2">
        <button className="btn btn-primary" onClick={install}>{s.installed ? 'התקן מחדש את האפליקציה' : 'התקן את האפליקציה ב-CRM'}</button>
        <button className="btn" onClick={() => run(api.sync, 'הסנכרון התחיל')} disabled={s.running}><Icons.Refresh size={16} /> סנכרן עכשיו</button>
        <button className="btn btn-danger" onClick={() => confirm('למחוק את המטמון ולבנות מחדש מה-CRM?') && run(api.rebuild, 'בנייה מחדש התחילה')} disabled={s.running}>בנה מחדש מאפס</button>
      </div>
    </section>
  );
}

/** Operator view: every client on the platform. */
export function TenantsPanel({ me, current, onSwitchTenant }: { me: Me; current: Tenant | null; onSwitchTenant: (id: string) => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [tenants, setTenants] = useState<Tenant[] | null>(null);
  const [form, setForm] = useState({ locationId: '', name: '', adminEmail: '' });
  const load = () => api.tenants().then(setTenants).catch((e) => toast(errorText(e), 'error'));
  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const create = async () => {
    try {
      await api.createTenant(form.locationId.trim(), form.name.trim(), form.adminEmail.trim() || undefined);
      setForm({ locationId: '', name: '', adminEmail: '' });
      await load(); await qc.invalidateQueries({ queryKey: ['me'] });
      toast('הלקוח נוצר — עכשיו התקן את האפליקציה בחשבון ה-CRM שלו');
    } catch (e) { toast(errorText(e), 'error'); }
  };
  const install = async (loc: string | null) => { try { const { url } = await api.installTenant(loc); location.href = url; } catch (e) { toast(errorText(e), 'error'); } };
  const remove = async (t: Tenant) => {
    if (!confirm(`למחוק את הלקוח "${t.name}" וכל המטמון שלו? הנתונים ב-CRM לא ייפגעו.`)) return;
    try { await api.deleteTenant(t.locationId); if (current?.locationId === t.locationId) setCurrentTenant(null); await load(); await qc.invalidateQueries({ queryKey: ['me'] }); toast('נמחק'); } catch (e) { toast(errorText(e), 'error'); }
  };
  return (
    <section className="card p-4">
      <h2 className="mb-1 text-base font-semibold">לקוחות (מפעיל המערכת)</h2>
      <p className="mb-3 text-xs text-ink-3">כל לקוח הוא חשבון-משנה אחד ב-CRM. אחרי יצירת הלקוח, לחץ "התקן" כדי לחבר את האפליקציה לחשבון שלו (בחר את החשבון הנכון במסך ההרשאה). מזהה החשבון מופיע בכתובת ה-CRM: <span className="ltr">/location/&lt;id&gt;/</span>.</p>
      <form className="mb-4 grid gap-2 desk:grid-cols-[1fr_1fr_1fr_auto]" onSubmit={(e) => { e.preventDefault(); void create(); }}>
        <input className="input ltr" dir="ltr" placeholder="מזהה חשבון CRM (location id)" value={form.locationId} onChange={(e) => setForm({ ...form, locationId: e.target.value })} required />
        <input className="input" placeholder="שם הלקוח" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <input className="input ltr" dir="ltr" type="email" placeholder="אימייל של מנהל הלקוח (אופציונלי)" value={form.adminEmail} onChange={(e) => setForm({ ...form, adminEmail: e.target.value })} />
        <button className="btn btn-primary" type="submit"><Icons.Plus size={16} /> צור לקוח</button>
      </form>
      {!tenants ? <Spinner /> : (
        <div className="-mx-4 overflow-x-auto px-4"><table className="w-full min-w-max text-sm">
          <thead><tr className="text-xs text-ink-3"><th className="py-1 text-start">לקוח</th><th className="text-start">מזהה</th><th className="text-start">מותקן</th><th /></tr></thead>
          <tbody>
            {tenants.map((t) => (
              <tr key={t.locationId} className={`border-t border-line ${current?.locationId === t.locationId ? 'bg-accent-soft/40' : ''}`}>
                <td className="py-2 font-medium">{t.name}</td>
                <td className="ltr text-ink-2">{t.locationId}</td>
                <td>{t.installedAt ? formatDateTime(t.installedAt) : <span className="text-danger">לא</span>}</td>
                <td className="text-end">
                  <span className="inline-flex gap-1">
                    {current?.locationId !== t.locationId && <button className="btn btn-sm" onClick={() => onSwitchTenant(t.locationId)}>פתח</button>}
                    <button className="btn btn-sm" onClick={() => install(t.locationId)}>{t.installedAt ? 'התקן מחדש' : 'התקן'}</button>
                    <button className="btn btn-sm btn-danger" onClick={() => remove(t)}>מחק</button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      <p className="mt-3 text-xs text-ink-3">מפעיל: <span className="ltr">{me.email}</span>. אפשר גם <button className="underline" onClick={() => install(null)}>להתקין בלי ליצור לקוח מראש</button> — הלקוח ייווצר אוטומטית לפי החשבון שייבחר במסך ההרשאה.</p>
    </section>
  );
}
